import React, { useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Minus } from "lucide-react"
import { useTranslation, Trans } from "react-i18next"
import BusinessManImg from "../../../../assets/avia/search/businessman.png"
import ChildrenImg from "../../../../assets/avia/search/children.png"
import BabyImg from "../../../../assets/avia/search/babis.png"
import { useClickOutside } from "../../../hooks"

const PassengerModal = ({ showModal, onClose, passengers, onUpdate }) => {
  const { t } = useTranslation()
  const { adults, children, infants } = passengers

  const modalRef = useRef(null)
  useClickOutside(modalRef, onClose)

  const updateCount = (type, delta) => {
    const current = passengers[type]
    const newValue = Math.max(0, current + delta)
    const total = adults + children + infants - current + newValue
    if (total === 0) return
    onUpdate(type, newValue)
  }

  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="absolute right-0 top-[60px] w-max min-w-[280px] bg-white rounded-lg shadow-lg z-10"
        >
          <CounterRow
            icon={BusinessManImg}
            label={t("admin.adult")}
            subtitle={<Trans i18nKey="admin.over_years" values={{ years: 14 }} />}
            value={adults}
            onIncrement={() => updateCount("adults", 1)}
            onDecrement={() => updateCount("adults", -1)}
          />
          <CounterRow
            icon={ChildrenImg}
            label={t("admin.child")}
            subtitle={<Trans i18nKey="admin.from_to_years" values={{ first: 2, second: 14 }} />}
            value={children}
            onIncrement={() => updateCount("children", 1)}
            onDecrement={() => updateCount("children", -1)}
          />
          <CounterRow
            icon={BabyImg}
            label={t("admin.infant")}
            subtitle={<Trans i18nKey="admin.from_to_years" values={{ first: 0, second: 2 }} />}
            value={infants}
            onIncrement={() => updateCount("infants", 1)}
            onDecrement={() => updateCount("infants", -1)}
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const CounterRow = ({ icon, label, subtitle, value, onIncrement, onDecrement }) => (
  <div className="flex items-center justify-between py-1 px-2">
    <div className="flex items-center gap-3">
      <img src={icon} alt="" className="w-5 h-5" />
      <div>
        <div className="text-dark-gray text-sm font-medium">{label}</div>
        {subtitle && <div className="text-xs text-[10px] text-light-gray">{subtitle}</div>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onDecrement}
        className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
        disabled={value === 0}
      >
        <Minus className="h-3 w-3 text-black" />
      </button>
      <span className="w-6 text-center text-[15px] font-medium text-app-content">{value}</span>
      <button
        onClick={onIncrement}
        className="w-5 h-5 rounded-full bg-app-base-strong/30 flex items-center justify-center hover:bg-app-base transition-colors cursor-pointer font-sans"
      >
        <Plus className="h-3 w-3 text-black" />
      </button>
    </div>
  </div>
)

export default PassengerModal
